import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocket } from "../hooks/useWebSocket";
import { useStreamingAnimation } from "../hooks/useStreamingAnimation";
import { useFileUpload } from "../hooks/useFileUpload";
import { useTextareaAutoGrow } from "../hooks/useTextareaAutoGrow";
import { chatService } from "../services/chatService";
import { ChatMessage, WebSocketMessage } from "../types/chat";
import { exportMessageToPDF } from "../utils/pdfExport";
import { formatTimestamp, formatFileSize } from "../utils/formatters";
import {
  convertCustomTableToMarkdown,
  renderMessageWithToolSearches,
  renderStreamSequence
} from "../utils/contentProcessing";
import PageHeader from "../components/PageHeader";
import ChatInput from "../components/ChatInput";
import ApprovalDialog from "../components/ApprovalDialog";
import InlineApproval from "../components/InlineApproval";
import "./ChatPage.css";

interface FileData {
  filename: string;
  content: string;
  size: number;
  type: string;
}

const ChatPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");

  // Use custom hooks
  const { selectedFiles, handleFilesSelected, removeFile, clearFiles } = useFileUpload();
  const textareaRef = useTextareaAutoGrow(inputMessage);
  const [aiStatus, setAiStatus] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [wasRecentlyConnected, setWasRecentlyConnected] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState<string>("");
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null
  );
  const [responseStats, setResponseStats] = useState<any>(null);
  const [reasoningContent, setReasoningContent] = useState<string>("");
  const [isReasoningVisible, setIsReasoningVisible] = useState<boolean>(true);
  const [activeReasoningMap, setActiveReasoningMap] = useState<
    Map<string, string>
  >(new Map());
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<
    string | null
  >(null);
  const [streamingThinking, setStreamingThinking] = useState<string>("");

  // Use streaming animation hooks
  const displayedText = useStreamingAnimation(streamingResponse, {
    charsPerInterval: 8,
    intervalMs: 10,
  });
  const displayedThinking = useStreamingAnimation(streamingThinking, {
    charsPerInterval: 12,
    intervalMs: 8,
  });

  // Timeline for tools and intermediate responses
  const [currentTimeline, setCurrentTimeline] = useState<
    Array<{
      type: "tool" | "intermediate_response";
      content: string;
    }>
  >([]);
  const [currentIntermediateResponse, setCurrentIntermediateResponse] =
    useState<string>("");
  const [showReasoningModal, setShowReasoningModal] = useState<{
    messageId: string;
    content: string;
  } | null>(null);

  // Approval state
  const [pendingApproval, setPendingApproval] = useState<any>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasLoadedMessagesRef = useRef<string | null>(null);

  const wsUrl = sessionId && token ? chatService.getWebSocketUrl(sessionId, token) : "";

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    //console.log("WebSocket message received:", message);

    switch (message.type) {
      case "connection_established":
        setError("");
        break;

      case "reasoning_chunk":
        // Handle reasoning chunks (thinking process)
        if (message.content) {
          console.log(
            "Received reasoning chunk:",
            message.content_type,
            message.content.substring(0, 50)
          );

          // Handle different reasoning content types
          if (message.content_type === "thinking_header") {
            // Start new reasoning section
            setReasoningContent(message.content);
            setStreamingThinking(message.content);
            setIsReasoningVisible(true);
          } else if (message.content_type === "thinking_chunk") {
            // Append thinking chunks with streaming effect
            setReasoningContent((prev) => prev + message.content);
            setStreamingThinking((prev) => prev + message.content);
          } else if (message.content_type === "thinking_footer") {
            // End reasoning section - save to map for message
            setReasoningContent((prev) => {
              const finalContent = prev + message.content;
              // Store reasoning for the current streaming message
              if (streamingMessageId || currentStreamingMessageId) {
                const msgId =
                  streamingMessageId || currentStreamingMessageId || "temp";
                setActiveReasoningMap((prevMap) => {
                  const newMap = new Map(prevMap);
                  newMap.set(msgId, finalContent);
                  return newMap;
                });
              }
              return finalContent;
            });
            setStreamingThinking((prev) => prev + message.content);
          } else if (message.content_type === "tool_start") {
            // Tool execution - add as a special marker that we'll format differently
            if (message.content) {
              console.log("Adding tool search marker:", message.content);
              // Extract tool name from message metadata if available
              const toolName = message.tool_name || "";

              // Initialize streaming message ID with a temporary ID if not set
              if (!streamingMessageId) {
                const tempId = `streaming-${Date.now()}`;
                setStreamingMessageId(tempId);
                setCurrentStreamingMessageId(tempId);
              }

              setStreamingResponse((prev) =>
                prev +
                "\n\n[TOOL_SEARCH:" +
                toolName +
                "]" +
                message.content +
                "[/TOOL_SEARCH]\n\n"
              );
              setIsLoading(true);
            }
          } else if (message.content_type === "response_header") {
            // Response header - add to streaming response
            if (message.content && message.content.trim()) {
              setStreamingResponse((prev) => prev + message.content);
            }
          } else if (message.content_type === "response_chunk") {
            // Response chunk - add to streaming response (actual content being streamed)
            if (message.content) {
              // Initialize streaming message ID with a temporary ID if not set
              if (!streamingMessageId) {
                const tempId = `streaming-${Date.now()}`;
                setStreamingMessageId(tempId);
                setCurrentStreamingMessageId(tempId);
              }

              setStreamingResponse((prev) => prev + message.content);
              setIsLoading(true);
            }
          } else if (message.content_type === "tool_result") {
            // Tool execution results - could add to timeline too
            setReasoningContent((prev) => prev + "\n" + message.content);
          }
        }
        break;
      case "completion":
        // Handle completion message - signals end of streaming
        console.log("Completion received:", message);

        // Update stats if present
        if (message.stats) {
          setResponseStats(message.stats);
          // Auto-hide stats after 5 seconds
          setTimeout(() => setResponseStats(null), 5000);
        }

        // Update streaming message ID with the actual message_id from completion
        if (message.message_id && streamingMessageId && streamingMessageId.startsWith("streaming-")) {
          const actualMessageId = message.message_id;
          // Update the reasoning map with the actual message ID
          setActiveReasoningMap((prevMap) => {
            const newMap = new Map(prevMap);
            if (prevMap.has(streamingMessageId)) {
              newMap.set(actualMessageId, prevMap.get(streamingMessageId)!);
              newMap.delete(streamingMessageId);
            }
            return newMap;
          });
          setCurrentStreamingMessageId(actualMessageId);
        }

        // End loading state since completion signals end of response
        setIsLoading(false);
        setAiStatus("");

        // Keep the streaming response visible until chat_messages arrives
        // If chat_messages doesn't arrive, the streaming message will remain visible
        break;

      case "response_stats":
        // Handle response completion statistics
        console.log("Response stats:", message.stats);
        if (message.stats) {
          setResponseStats(message.stats);
          // Auto-hide stats after 5 seconds
          setTimeout(() => setResponseStats(null), 5000);
        }

        // Update streaming message ID with the actual message_id from response_stats
        if (message.message_id && streamingMessageId && streamingMessageId.startsWith("streaming-")) {
          const actualMessageId = message.message_id;
          // Update the reasoning map with the actual message ID
          setActiveReasoningMap((prevMap) => {
            const newMap = new Map(prevMap);
            if (prevMap.has(streamingMessageId)) {
              newMap.set(actualMessageId, prevMap.get(streamingMessageId)!);
              newMap.delete(streamingMessageId);
            }
            return newMap;
          });
          setCurrentStreamingMessageId(actualMessageId);
        }
        break;

      case "chat_messages":
        if (message.messages) {
          console.log(
            "Received final messages:",
            message.messages.length,
            "messages"
          );

          // Map reasoning from temp/streaming ID to actual message ID
          const newMessages = message.messages;
          const lastAssistantMessage = newMessages
            .filter((m) => m.message_type === "assistant")
            .pop();

          if (
            lastAssistantMessage &&
            currentStreamingMessageId &&
            reasoningContent
          ) {
            setActiveReasoningMap((prevMap) => {
              const newMap = new Map(prevMap);
              // Move reasoning from temp ID to actual message ID
              if (prevMap.has(currentStreamingMessageId)) {
                newMap.set(
                  lastAssistantMessage.id,
                  prevMap.get(currentStreamingMessageId)!
                );
                newMap.delete(currentStreamingMessageId);
              } else if (prevMap.has("temp")) {
                newMap.set(lastAssistantMessage.id, prevMap.get("temp")!);
                newMap.delete("temp");
              } else {
                // Store current reasoning content
                newMap.set(lastAssistantMessage.id, reasoningContent);
              }
              return newMap;
            });
          }

          // Clear streaming state when final messages arrive
          setStreamingResponse("");
          setStreamingMessageId(null);
          setCurrentStreamingMessageId(null);
          setStreamingThinking("");
          setCurrentTimeline([]);
          setCurrentIntermediateResponse("");
          // Don't clear reasoning content - keep it visible after response completes

          // Merge with existing messages
          setMessages((prevMessages) => {
            // Remove temporary messages (they start with "temp-")
            const nonTempMessages = prevMessages.filter(
              (msg) => !msg.id.startsWith("temp-")
            );

            // Create a map of existing messages by ID for fast lookup
            const existingMessagesMap = new Map(
              nonTempMessages.map((msg) => [msg.id, msg])
            );

            // Add new messages to the map (this will overwrite any existing ones with same ID)
            message.messages!.forEach((msg) => {
              existingMessagesMap.set(msg.id, msg);
            });

            // Convert back to array and sort by created_at to maintain chronological order
            const mergedMessages = Array.from(
              existingMessagesMap.values()
            ).sort(
              (a, b) =>
                new Date(a.created_at).getTime() -
                new Date(b.created_at).getTime()
            );

            console.log("Merged messages count:", mergedMessages.length);
            return mergedMessages;
          });

          clearFiles();
          setInputMessage("");
        }
        setIsLoading(false);
        setAiStatus(""); // Clear AI status when conversation completes
        break;

      case "ai_status":
        const statusMessage = message.message || "";
        setAiStatus(statusMessage);

        // Handle different AI status types
        const status = message.status;
        if (status === "error") {
          setIsLoading(false);
          setStreamingResponse("");
          setStreamingMessageId(null);
          setCurrentStreamingMessageId(null);
        } else if (
          status === "processing" ||
          status === "thinking" ||
          status === "analyzing" ||
          status === "searching" ||
          status === "processing_files" ||
          status === "generating_plan" ||
          status === "plan_ready"
        ) {
          setIsLoading(true);
          // Start fresh streaming response
          if (!streamingResponse) {
            setStreamingResponse("");
          }
        } else if (status === "completed") {
          // Keep loading state until final messages arrive
        }
        break;

      case "error":
        setError(message.error || "An error occurred");
        setIsLoading(false);
        setAiStatus("");
        setStreamingResponse("");
        setStreamingMessageId(null);
        break;

      case "approval_required":
        // Handle approval request from backend
        console.log("Approval required:", message.approval_data);
        if (message.approval_data) {
          setPendingApproval(message.approval_data);
          // Don't show modal - approval will be displayed inline
          // setShowApprovalDialog(true);
        }
        break;
    }
  };

  const handleWebSocketConnect = () => {
    console.log("Connected to chat session:", sessionId);
    setError("");
    setWasRecentlyConnected(true);

    // Reset the flag after a short delay to avoid stale state
    setTimeout(() => {
      setWasRecentlyConnected(false);
    }, 5000);

    // The backend should automatically send existing messages after connection
    // If not, we might need to request them explicitly
    console.log("Connected - waiting for existing messages from backend");
  };

  const handleWebSocketDisconnect = () => {
    console.log("Disconnected from chat session");
    setAiStatus("");
  };

  const handleWebSocketError = (error: Event) => {
    console.error("WebSocket error:", error);
    setError("Connection error occurred");
  };

  const {
    isConnected,
    isConnecting,
    sendMessage,
    sendApprovalResponse,
    reconnect,
    error: wsError,
  } = useWebSocket({
    url: wsUrl,
    onMessage: handleWebSocketMessage,
    onConnect: handleWebSocketConnect,
    onDisconnect: handleWebSocketDisconnect,
    onError: handleWebSocketError,
  });

  // Approval handlers
  const handleApprove = () => {
    console.log("User approved the API call");
    sendApprovalResponse(true);
    setPendingApproval(null);
  };

  const handleReject = () => {
    console.log("User rejected the API call");
    sendApprovalResponse(false);
    setPendingApproval(null);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [inputMessage]);

  // Load existing messages when sessionId changes
  useEffect(() => {
    const loadExistingMessages = async () => {
      if (!sessionId || !token) {
        hasLoadedMessagesRef.current = null;
        return;
      }

      // Prevent duplicate requests in StrictMode
      if (hasLoadedMessagesRef.current === sessionId) {
        console.log("Messages already loaded for session:", sessionId);
        return;
      }

      try {
        setIsLoading(true);
        setError("");
        console.log("Loading existing messages for session:", sessionId);
        hasLoadedMessagesRef.current = sessionId;

        const data = await chatService.getChatMessages(token, sessionId);

        if (data.messages && Array.isArray(data.messages)) {
          console.log("Loaded", data.messages.length, "existing messages");
          setMessages(data.messages);

          // Extract reasoning from workflow_metadata for assistant messages
          const newReasoningMap = new Map<string, string>();
          data.messages.forEach((msg: ChatMessage) => {
            if (
              msg.message_type === "assistant" &&
              msg.workflow_metadata?.reasoning_visible
            ) {
              newReasoningMap.set(
                msg.id,
                msg.workflow_metadata.reasoning_visible
              );
            }
          });

          if (newReasoningMap.size > 0) {
            console.log(
              `Loaded reasoning for ${newReasoningMap.size} messages`
            );
            setActiveReasoningMap(newReasoningMap);
          }
        } else {
          console.log("No existing messages found or invalid format");
          setMessages([]);
        }

        // Check for pending approval (e.g., after page refresh)
        if (data.pending_approval?.approval_data) {
          console.log("Restoring pending approval:", data.pending_approval);
          setPendingApproval(data.pending_approval.approval_data);
        }
      } catch (err) {
        console.error("Failed to load existing messages:", err);
        setError("Failed to load chat history");
        setMessages([]);
        hasLoadedMessagesRef.current = null; // Reset on error to allow retry
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingMessages();
  }, [sessionId, token]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = () => {
    if ((!inputMessage.trim() && selectedFiles.length === 0) || isLoading) {
      return;
    }

    console.log(
      "Attempting to send message. Connected:",
      isConnected,
      "Connecting:",
      isConnecting
    );

    setIsLoading(true);
    setError("");

    // Clear previous reasoning content and timeline when sending new message
    setReasoningContent("");
    setIsReasoningVisible(false);
    setStreamingThinking("");
    setCurrentTimeline([]);
    setCurrentIntermediateResponse("");

    const messageData =
      selectedFiles.length > 0
        ? {
            type: "file_upload",
            message: inputMessage.trim(),
            files: selectedFiles.map((file) => ({
              filename: file.filename,
              content: file.content,
              size: file.size,
            })),
          }
        : {
            type: "chat_message",
            message: inputMessage.trim(),
          };

    // Add user message immediately to UI (optimistic update)
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      session_id: sessionId!,
      message_type: "user",
      content: inputMessage.trim(),
      created_at: new Date().toISOString(),
      status: "pending",
      attachments:
        selectedFiles.length > 0
          ? selectedFiles.map((file, idx) => ({
              id: `temp-attachment-${idx}`,
              document_name: file.filename,
              document_type: file.type,
              document_size: file.size,
              // Also include legacy fields for backwards compatibility
              filename: file.filename,
              file_type: file.type,
              file_size: file.size,
            }))
          : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);

    const success = sendMessage(messageData);
    if (!success) {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };



  const handleExportMessage = (message: ChatMessage) => {
    const sessionTitle = `Chat Session - ${sessionId}`;
    exportMessageToPDF(message, sessionTitle);
  };

  if (!sessionId) {
    return <div className="chat-error">Invalid session ID</div>;
  }

  return (
    <div className="chat-container">
      <PageHeader
        title="AI Assistant"
        showBackButton={true}
        backTo="/chats"
        username={user?.username || "User"}
        isSuperuser={user?.is_superuser}
        onLogout={async () => {
          await logout();
          navigate("/login");
        }}
        onManageKnowledge={() => navigate("/knowledge-documents")}
        onManageUsers={() => navigate("/manage-users")}
        rightContent={
          <div className="connection-status">
            {isConnecting && (
              <span className="status connecting">Connecting...</span>
            )}
            {isConnected && <span className="status connected">Connected</span>}
            {!isConnected && !isConnecting && (
              <button onClick={reconnect} className="reconnect-button">
                Reconnect
              </button>
            )}
          </div>
        }
      />

      <main className="chat-main">
        <div className="messages-container">
          {isLoading && messages.length === 0 && (
            <div className="empty-chat">
              <h3>Loading chat history...</h3>
              <p>Please wait while we load your messages</p>
            </div>
          )}

          {messages.length === 0 && !isLoading && (
            <div className="empty-chat">
              <h3>Start a conversation</h3>
              <p>Ask me anything or upload files to analyze!</p>
            </div>
          )}

          {messages.map((message) => (
            <React.Fragment key={message.id}>
              {/* Thinking display - appears before assistant messages */}
              {message.message_type === "assistant" &&
                activeReasoningMap.has(message.id) && (
                  <div className="thought-process-container">
                    <div
                      className="thought-process-header"
                      onClick={() => {
                        const thinkingId = `thinking-${message.id}`;
                        const element = document.getElementById(thinkingId);
                        if (element) {
                          const isExpanded = element.style.display !== "none";
                          element.style.display = isExpanded ? "none" : "block";
                          const icon =
                            element.previousElementSibling?.querySelector(
                              ".thought-toggle-icon"
                            );
                          if (icon) {
                            icon.textContent = isExpanded ? "✨" : "✨";
                          }
                        }
                      }}
                    >
                      <span className="thought-toggle-icon">✨</span>
                      <span>Reasoning</span>
                    </div>
                    <div
                      id={`thinking-${message.id}`}
                      className="thought-process-content"
                      style={{ display: "none" }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {activeReasoningMap.get(message.id) || ""}
                      </ReactMarkdown>

                      {/* Tool Executions */}
                      {message.workflow_metadata?.tool_executions &&
                        message.workflow_metadata.tool_executions.length >
                          0 && (
                          <div className="tool-executions">
                            <h4>🔧 Tool Executions:</h4>
                            {message.workflow_metadata.tool_executions.map(
                              (execution, idx) => (
                                <div key={idx} className="tool-execution-item">
                                  <div className="tool-name">
                                    <strong>{execution.tool_name}</strong>
                                  </div>
                                  {execution.step && (
                                    <div className="tool-step">
                                      <em>{execution.step}</em>
                                    </div>
                                  )}
                                  {execution.arguments && (
                                    <div className="tool-arguments">
                                      <code>
                                        {JSON.stringify(
                                          execution.arguments,
                                          null,
                                          2
                                        )}
                                      </code>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                )}

              <div className={`message ${message.message_type}`}>
                <div className="message-wrapper">
                  <div className="message-content">
                    <div className="message-text">
                      {/* If stream_sequence exists, render with timeline, otherwise just content */}
                      {message.workflow_metadata?.stream_sequence ? (
                        renderStreamSequence(
                          message.workflow_metadata.stream_sequence,
                          message.content
                        )
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {convertCustomTableToMarkdown(message.content)}
                        </ReactMarkdown>
                      )}
                    </div>
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="message-attachments">
                        {message.attachments.map((attachment, index) => {
                          const fileName = attachment.document_name || attachment.filename || 'Unknown file';
                          const fileSize = attachment.document_size || attachment.file_size || 0;
                          return (
                            <div key={index} className="attachment">
                              📎 {fileName} ({formatFileSize(fileSize)})
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="message-actions">
                    <button
                      onClick={() => handleExportMessage(message)}
                      className="export-button"
                      title="Export as PDF"
                    >
                      📄
                    </button>
                  </div>
                </div>
                <div className="message-timestamp">
                  {formatTimestamp(message.created_at)}
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* AI Status display */}
          {aiStatus && !streamingResponse && !streamingThinking && (
            <div className="ai-status">
              <div className="status-indicator"></div>
              <span>{aiStatus}</span>
            </div>
          )}

          {/* Streaming thinking display in collapsible section */}
          {streamingThinking && displayedThinking && (
            <div className="thought-process-container live-thinking">
              <div className="thought-process-header">
                <span className="thought-toggle-icon">✨</span>
                <span>Reasoning</span>
              </div>
              <div className="thought-process-content" style={{ display: "block" }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {displayedThinking}
                </ReactMarkdown>
                <span className="streaming-cursor">▌</span>
              </div>
            </div>
          )}

          {/* Streaming response display - includes everything: intermediate responses, tools, final response */}
          {streamingResponse && streamingMessageId && (
            <div className="message assistant streaming">
              <div className="message-wrapper">
                <div className="message-content">
                  <div className="message-text">
                    {renderMessageWithToolSearches(streamingResponse, true)}
                    <span className="streaming-cursor">▌</span>
                  </div>
                </div>
              </div>
              <div className="message-timestamp">Streaming...</div>
            </div>
          )}

          {/* Inline Approval Display */}
          {pendingApproval && (
            <div className="message assistant">
              <div className="message-wrapper">
                <div className="message-content">
                  <InlineApproval
                    approvalData={pendingApproval}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Response stats display */}
          {responseStats && (
            <div className="response-stats">
              {responseStats.tokens && (
                <span>Tokens: {responseStats.tokens}</span>
              )}
              {responseStats.response_time && (
                <span>Time: {responseStats.response_time}s</span>
              )}
              {responseStats.sources_used && (
                <span>Sources: {responseStats.sources_used}</span>
              )}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {error && <div className="chat-error-banner">{error}</div>}

        <ChatInput
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          selectedFiles={selectedFiles}
          onFilesSelected={handleFilesSelected}
          onRemoveFile={removeFile}
          onSendMessage={handleSendMessage}
          onKeyPress={handleKeyPress}
          isLoading={isLoading}
          textareaRef={textareaRef}
        />
      </main>

      {/* Approval Dialog - Kept as backup, but using inline approvals by default */}
      {/* <ApprovalDialog
        isOpen={showApprovalDialog}
        approvalData={pendingApproval}
        onApprove={handleApprove}
        onReject={handleReject}
      /> */}
    </div>
  );
};

export default ChatPage;
